// public/js/utils/asset-utils.js

/**
 * Format a UTC ISO timestamp in the user's own locale and local timezone.
 * The browser derives both automatically via navigator.language + system timezone.
 * @param {string} isoString  UTC ISO-8601 string (e.g. "2024-01-15T10:30:00.000Z")
 * @param {object} [extraOpts]  Additional Intl.DateTimeFormat options
 */
export function formatLocalTime(isoString, extraOpts = {}) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString(navigator.language, {
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    ...extraOpts,
  });
}

/**
 * Human-friendly "time since" a timestamp, auto-picking the unit:
 * seconds < 60s, minutes < 60m, hours < 24h, otherwise days.
 * @param {string} isoString  UTC ISO-8601 string
 * @returns {string} e.g. "just now", "5 seconds ago", "3 minutes ago", "2 hours ago", "4 days ago"
 */
export function formatTimeAgo(isoString) {
  if (!isoString) return '—';
  const ms = Date.now() - new Date(isoString).getTime();
  if (!Number.isFinite(ms)) return '—';
  if (ms < 0) return 'just now';

  const sec = Math.round(ms / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec} seconds ago`;

  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;

  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function formatAssetLabel(asset) {
    if (!asset) return '—';
    const label = asset.label || asset.name || 'Unnamed Asset';
    const ref = asset.reference || asset.serial || '';
    return ref ? `${label} / ${ref}` : label;
  }
  
  export function formatAssetLabelShort(asset, maxLen = 28) {
    const full = formatAssetLabel(asset);
    return full.length > maxLen ? full.substring(0, maxLen - 1) + '…' : full;
  }
  
  export function getAssetId(asset) {
    return asset?.assetUUID || asset?.assetId || asset?.id || null;
  }
  
  export function getAssetTooltip(asset) {
    const display = formatAssetLabel(asset);
    const uuidShort = (asset.assetUUID || '').substring(0, 8);
    // Native title tooltips can't be CSS-blurred, so mask the tenant in demo mode.
    const tenant = asset.tenantName || asset.tenantId || '—';
    return `${display}\nUUID: ${uuidShort}…\nTenant: ${tenant}\nStatus: ${asset.status || 'active'}`;
  }